import { action, computed, makeObservable, observable } from 'mobx';
import { errorHandler } from '../../../../utils/errors';
import { AccountSaveArgs, AccountSaveMethod } from '../types';
import Service, { ServiceDto } from './Service';
import { ServiceProvider } from './types';

export default abstract class Services<T extends Service> {
  items: T[] = [];

  selectedProvider?: ServiceProvider = undefined;

  constructor(
    dto: ServicesDto = [],
    selectedProvider?: ServiceProvider,
    private readonly onSave?: AccountSaveMethod,
  ) {
    makeObservable(this, {
      items: observable,
      selectedProvider: observable,
      latest: computed,
      selectedServices: computed,
      addService: action.bound,
      save: action.bound,
      setSelectedProvider: action.bound,
      ...({
        unserialize: action.bound,
      } as any),
    });

    this.items = dto.map((serviceDto) => this.unserialize(serviceDto));

    const latestItem = this.items.slice(-1)?.[0];
    this.selectedProvider = selectedProvider || latestItem?.provider;
  }

  get latest(): T | undefined {
    if (this.items.length > 0) {
      return this.items.at(-1);
    }

    return undefined;
  }

  get selectedServices(): T[] {
    const items = this.filterByProvider(this.selectedProvider);
    if (items.length > 0) {
      return items;
    }

    if (this.selectedProvider) {
      return [this.unserialize({ provider: this.selectedProvider })];
    }

    return [];
  }

  addService(provider: ServiceProvider) {
    const service = this.unserialize({ provider });
    this.items.push(service);
    return service;
  }

  toJSON(): Required<ServicesDto> {
    return this.items.map((item) => item.toJSON());
  }

  setSelectedProvider(provider: ServiceProvider) {
    this.selectedProvider = provider;
    this.save().catch(errorHandler('Cannot save service config'));
  }

  async save(options: AccountSaveArgs = {}) {
    if (options.service) {
      this.onSaveService(options.service as T, options.reason);
    }

    await this.onSave?.(options);
  }

  findByProvider<TProvider = T>(
    provider: ServiceProvider,
  ): TProvider | undefined {
    return this.items.find((s) => s.provider === provider) as TProvider;
  }

  filterByProvider(provider?: ServiceProvider): T[] {
    if (!provider) {
      return [];
    }

    return this.items.filter((s) => s.provider === provider);
  }

  protected abstract unserialize(dto: ServiceDto): T;

  private onSaveService(service: T, reason?: string) {
    if (reason === 'delete') {
      this.items = this.items.filter((item) => item !== service);
      return;
    }

    if (!this.items.includes(service)) {
      this.items.push(service);
    }
  }
}

export type ServicesDto = ServiceDto[];
